import React, { useCallback, useMemo } from 'react';
import {
  WatermarkConfig,
  WatermarkPosition,
  WatermarkType,
  WATERMARK_FONT_OPTIONS,
  WATERMARK_POSITION_OPTIONS,
} from '../../../../types/watermark';

interface WatermarkSettingsProps {
  config: WatermarkConfig;
  onChange: (config: WatermarkConfig) => void;
  photographerName?: string;
  className?: string;
}

const WatermarkSettings: React.FC<WatermarkSettingsProps> = ({
  config,
  onChange,
  photographerName,
  className = '',
}) => {
  const handleChange = useCallback(
    <K extends keyof WatermarkConfig>(key: K, value: WatermarkConfig[K]) => {
      onChange({
        ...config,
        [key]: value,
      });
    },
    [config, onChange]
  );

  const defaultText = useMemo(() => {
    const year = new Date().getFullYear();
    return photographerName ? `\u00A9 ${year} ${photographerName}` : `\u00A9 ${year} PixelShare`;
  }, [photographerName]);

  const displayText = config.text || defaultText;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900">Watermark Protection</h4>
          <p className="text-sm text-gray-500">
            Add a watermark to protect your photos from unauthorized use
          </p>
        </div>
        <button
          type="button"
          onClick={() => handleChange('enabled', !config.enabled)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            config.enabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
          role="switch"
          aria-checked={config.enabled}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              config.enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {config.enabled && (
        <>
          {/* Watermark Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Watermark Type
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="watermarkType"
                  value="text"
                  checked={config.type === 'text'}
                  onChange={() => handleChange('type', 'text' as WatermarkType)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Text</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="watermarkType"
                  value="image"
                  checked={config.type === 'image'}
                  onChange={() => handleChange('type', 'image' as WatermarkType)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Logo/Image</span>
              </label>
            </div>
          </div>

          {/* Text Watermark Options */}
          {config.type === 'text' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Watermark Text
                </label>
                <input
                  type="text"
                  value={config.text || ''}
                  onChange={(e) => handleChange('text', e.target.value)}
                  placeholder={defaultText}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to use: {defaultText}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Font
                  </label>
                  <select
                    value={config.fontFamily}
                    onChange={(e) => handleChange('fontFamily', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    {WATERMARK_FONT_OPTIONS.map((font) => (
                      <option key={font.value} value={font.value}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Font Size
                  </label>
                  <input
                    type="number"
                    min={12}
                    max={144}
                    value={config.fontSize}
                    onChange={(e) => handleChange('fontSize', parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Text Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={config.fontColor}
                    onChange={(e) => handleChange('fontColor', e.target.value)}
                    className="h-10 w-10 p-0 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.fontColor}
                    onChange={(e) => handleChange('fontColor', e.target.value)}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Image Watermark Options */}
          {config.type === 'image' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Watermark Logo
                </label>
                {config.imagePreviewUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={config.imagePreviewUrl}
                      alt="Watermark preview"
                      className="max-h-24 max-w-48 border border-gray-300 rounded"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        handleChange('imageS3Key', undefined);
                        handleChange('imagePreviewUrl', undefined);
                      }}
                      className="absolute -top-2 -right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="mt-1 text-sm text-gray-600">
                        Upload a PNG logo with transparency
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG recommended, max 2MB
                      </p>
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              handleChange('imagePreviewUrl', reader.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Position Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position
            </label>
            <div className="grid grid-cols-3 gap-2 max-w-xs">
              {WATERMARK_POSITION_OPTIONS.filter(p => p.value !== 'tile').map((pos) => (
                <button
                  key={pos.value}
                  type="button"
                  onClick={() => handleChange('position', pos.value as WatermarkPosition)}
                  className={`p-3 border rounded-md text-center transition-colors ${
                    config.position === pos.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  title={pos.label}
                >
                  <span className="text-lg">{pos.icon}</span>
                </button>
              ))}
            </div>
            <label className="flex items-center mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.position === 'tile'}
                onChange={(e) => handleChange('position', e.target.checked ? 'tile' : 'bottom-right')}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Tile watermark across entire image
              </span>
            </label>
          </div>

          {/* Opacity Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opacity: {Math.round(config.opacity * 100)}%
            </label>
            <input
              type="range"
              min={10}
              max={100}
              value={Math.round(config.opacity * 100)}
              onChange={(e) => handleChange('opacity', parseInt(e.target.value, 10) / 100)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Subtle</span>
              <span>Strong</span>
            </div>
          </div>

          {/* Scale Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Size: {Math.round(config.scale * 100)}% of image width
            </label>
            <input
              type="range"
              min={5}
              max={50}
              value={Math.round(config.scale * 100)}
              onChange={(e) => handleChange('scale', parseInt(e.target.value, 10) / 100)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Small</span>
              <span>Large</span>
            </div>
          </div>

          {/* Margin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Margin from edge: {config.margin}px
            </label>
            <input
              type="range"
              min={0}
              max={100}
              value={config.margin}
              onChange={(e) => handleChange('margin', parseInt(e.target.value, 10))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
          </div>

          {/* Preview */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
            <div className="relative bg-gray-800 rounded-lg overflow-hidden" style={{ height: '200px' }}>
              <div
                className="absolute text-white pointer-events-none"
                style={{
                  fontFamily: config.fontFamily,
                  fontSize: `${config.fontSize * 0.5}px`,
                  color: config.fontColor,
                  opacity: config.opacity,
                  ...(config.position === 'center' && {
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }),
                  ...(config.position === 'top-left' && {
                    top: `${config.margin * 0.5}px`,
                    left: `${config.margin * 0.5}px`,
                  }),
                  ...(config.position === 'top-right' && {
                    top: `${config.margin * 0.5}px`,
                    right: `${config.margin * 0.5}px`,
                  }),
                  ...(config.position === 'bottom-left' && {
                    bottom: `${config.margin * 0.5}px`,
                    left: `${config.margin * 0.5}px`,
                  }),
                  ...(config.position === 'bottom-right' && {
                    bottom: `${config.margin * 0.5}px`,
                    right: `${config.margin * 0.5}px`,
                  }),
                  ...(config.position === 'tile' && {
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                  }),
                }}
              >
                {config.type === 'text' ? (
                  displayText
                ) : config.imagePreviewUrl ? (
                  <img
                    src={config.imagePreviewUrl}
                    alt="Watermark"
                    style={{
                      maxHeight: '60px',
                      opacity: config.opacity,
                    }}
                  />
                ) : (
                  <span className="text-gray-400 italic">No logo uploaded</span>
                )}
              </div>
              <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm">
                Sample Image Area
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WatermarkSettings;
